/**
 * StartScreen.tsx —— 启动页 + 开局设置（PRD 模块 3 / AC3.1–3.4）
 *
 * 校验策略（QA-BUG-030 根因整改）：
 *   改前每个数字框都在 `onChange` 里 `clampInt(...)` 直接吞掉用户输入 ——
 *   `''`/`abc` 静默回退旧值、`0`/`-3` 静默变成 1、`999999` 静默变成 9、`2.5` 静默四舍五入。
 *   非法态在 state 里根本不存在，于是 `aria-invalid` 恒 false、错误文案永不出现、
 *   「开始对局」永不禁用（QA 6/6 组非法输入零校验，还倒过来抱怨「钳制方向错误」）。
 *
 *   改后每个数字框都保存**用户原样输入的字符串**，由 `parseIntField` 解析出
 *   `空 / 非数字 / 小数 / 越界` 四类具体原因：
 *     - 非法态一律 `aria-invalid=true` + `aria-describedby` 指向字段级原因文案 + 禁用开始按钮；
 *     - 只有「数值合法但越界」这一类在 blur 时钳制到最近的合法边界，并且**显式播报**
 *       钳制结果（`.field__clamp`，role=status），绝不静默改写用户意图；
 *     - 跨字段规则（盲注 / 前注 / 带入的相互关系）仍然只由引擎 `validateConfig` 判定，
 *       UI 不另写一套规则。
 */

import { useId, useMemo, useState } from 'react';
import { validateConfig, type GameConfig } from '../core/game';
import { STYLES, STYLE_KEYS, type StyleKey } from '../core/ai/styles';
import type { SetupParams } from '../state/controller';
import { summarize, type LoadResult } from '../state/persistence';
import { chips, relativeTime } from '../state/format';
import { Segmented } from './primitives';
import { VERSION_LABEL } from '../version';

const PRESETS = [
  { key: 'quick', label: '快速开局', desc: '3 人桌 · 盲注 10/20 · 带入 1,000', opponents: 2, stack: 1000, sb: 10, bb: 20 },
  { key: 'six', label: '六人桌', desc: '6 人桌 · 盲注 25/50 · 带入 5,000', opponents: 5, stack: 5000, sb: 25, bb: 50 },
  { key: 'eight', label: '八人满桌', desc: '8 人桌 · 盲注 25/50 · 带入 5,000', opponents: 7, stack: 5000, sb: 25, bb: 50 },
  { key: 'heads', label: '单挑', desc: '2 人桌 · 盲注 50/100 · 带入 10,000', opponents: 1, stack: 10000, sb: 50, bb: 100 },
] as const;

/**
 * Round21-M1 · T4 / AC-Z3：2~8 人「零键盘开局」路径。
 * 预设芯片覆盖 2/3/6/8 人，4/5/7 人此前必须手输对手数量（聚焦 input → iOS auto-zoom）。
 * 这里提供一个 −/+ 步进器，覆盖 total ∈ [2,8] 的每一档，全程不聚焦任何 input（AC-Z3：对每个
 * n∈[2,8] 存在一条不聚焦 input 的路径进入牌桌）。总人数 = 对手数 + 1，故对手数域 [1,7]。
 * Round23 §4.1.5：竖屏 360/375 下 9~10 人全员亮牌摊牌零遮挡物理不可解，人数上限由 10 降到 8。
 */
const TOTAL_MIN = 2;
const TOTAL_MAX = 8;

const PHASE_CN: Record<string, string> = {
  idle: '等待开手',
  preflop: '翻牌前',
  flop: '翻牌圈',
  turn: '转牌圈',
  river: '河牌圈',
  showdown: '摊牌',
  handComplete: '本手已结束',
  gameOver: '对局已结束',
};

export function StartScreen({
  save,
  onStart,
  onResume,
  onDiscardSave,
  onEnterMultiplayer,
}: {
  save: LoadResult;
  onStart: (p: SetupParams) => void;
  onResume: () => void;
  onDiscardSave: () => void;
  onEnterMultiplayer?: () => void;
}) {
  const [oppText, setOppText] = useState('2');
  const [stackText, setStackText] = useState('1000');
  const [sbText, setSbText] = useState('10');
  const [bbText, setBbText] = useState('20');
  const [anteText, setAnteText] = useState('0');
  const [seedText, setSeedText] = useState('');
  /** 钳制播报：字段 key → 文案（仅「数值合法但越界」时出现）。 */
  const [clamped, setClamped] = useState<Record<string, string>>({});
  /** 钳制结果是否已被用户确认过一次（REG2-NEW-001，见 submit）。 */
  const [clampAck, setClampAck] = useState(false);
  /**
   * R2（聚焦清空默认值）：每个数字框维护「pristine（值仍是默认/预设值）」标记与「当前默认值」。
   *   - pristine：初始 / 预设写入 / blur 空回填后为 true；用户任一次键入置 false（dirty）；
   *   - defaults：该框当前的默认值文案（= placeholder 数值 = blur 空时回填的值），预设会改写它；
   *   - restored：blur 空回填后的「已恢复默认 N」播报（role=status）。
   * 红线（QA-BUG-030）：聚焦清空只在 pristine（用户没编辑过）时发生 = 省一步删除，不是改写用户意图；
   * 一旦 dirty，非法值仍按既有红字 / 钳制口径处理，不回退本轮任何校验。
   */
  const [pristine, setPristine] = useState<Record<FieldKey, boolean>>({
    opponents: true,
    stack: true,
    sb: true,
    bb: true,
    ante: true,
  });
  const [defaults, setDefaults] = useState<Record<FieldKey, string>>({
    opponents: '2',
    stack: '1000',
    sb: '10',
    bb: '20',
    ante: '0',
  });
  const [restored, setRestored] = useState<Record<string, string>>({});
  const [styleMode, setStyleMode] = useState<'random' | 'manual'>('random');
  const [styles, setStyles] = useState<StyleKey[]>(Array.from({ length: 7 }, () => 'tag'));
  const [advanced, setAdvanced] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const seedValue = seedText.trim() === '' ? null : Number(seedText.trim());
  const seedInvalid = seedText.trim() !== '' && (!Number.isFinite(seedValue) || !Number.isInteger(seedValue) || (seedValue as number) < 0);

  const uid = useId();
  const errId = (k: string): string => `${uid}-${k}-err`;

  /**
   * R2：pristine 且被聚焦清空（显示为空、placeholder 亮着默认值）的框，**校验按默认值算**——
   * 它语义上「仍是默认值」，不应因为聚焦清空这一步就红。用户一旦键入（dirty），即按原样输入校验。
   */
  const effText = useMemo(
    () => ({
      opponents: pristine.opponents && oppText.trim() === '' ? defaults.opponents : oppText,
      stack: pristine.stack && stackText.trim() === '' ? defaults.stack : stackText,
      sb: pristine.sb && sbText.trim() === '' ? defaults.sb : sbText,
      bb: pristine.bb && bbText.trim() === '' ? defaults.bb : bbText,
      ante: pristine.ante && anteText.trim() === '' ? defaults.ante : anteText,
    }),
    [pristine, defaults, oppText, stackText, sbText, bbText, anteText],
  );

  /** 字段级解析结果（原样输入 → 值 / 具体非法原因）。 */
  const parsed = useMemo(
    () => ({
      opponents: parseIntField(effText.opponents, FIELD_RANGE.opponents),
      stack: parseIntField(effText.stack, FIELD_RANGE.stack),
      sb: parseIntField(effText.sb, FIELD_RANGE.sb),
      bb: parseIntField(effText.bb, FIELD_RANGE.bb),
      ante: parseIntField(effText.ante, FIELD_RANGE.ante),
    }),
    [effText],
  );

  /** 解析通过后的取值（供预览文案与提交使用；非法时为 null）。 */
  const opponents = parsed.opponents.value;
  const stack = parsed.stack.value;
  const sb = parsed.sb.value;
  const bb = parsed.bb.value;
  const ante = parsed.ante.value;
  /** 手动风格列表 / 大盲倍数等预览用的安全取值。 */
  const oppSafe = opponents ?? 0;

  const fieldErrors = useMemo(() => {
    const e: Record<string, string> = {};
    for (const k of FIELD_ORDER) {
      const r = parsed[k];
      if (r.error) e[k] = r.error;
    }
    // 字段自身都合法，才谈得上跨字段规则；否则引擎会拿 NaN 判出误导性原因
    if (Object.keys(e).length === 0) {
      const cfg: GameConfig = {
        seatCount: (opponents as number) + 1,
        startingStack: stack as number,
        smallBlind: sb as number,
        bigBlind: bb as number,
        ante: ante as number,
        humanSeat: 0,
      };
      try {
        validateConfig(cfg);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        if (/带入筹码必须|大盲.*必须小于带入|前注 \+ 大盲/.test(msg)) e.stack = pickLine(msg, ['带入筹码', '必须小于带入筹码', '前注 + 大盲']);
        if (/小盲/.test(msg)) e.sb = pickLine(msg, ['小盲']);
        if (/大盲/.test(msg)) e.bb = pickLine(msg, ['大盲']);
        if (/前注/.test(msg)) e.ante = pickLine(msg, ['前注']);
        if (/总人数/.test(msg)) e.opponents = pickLine(msg, ['总人数']);
      }
    }
    if (seedInvalid) e.seed = '种子需为非负整数，留空表示随机';
    return e;
  }, [parsed, opponents, stack, sb, bb, ante, seedInvalid]);

  const invalid = Object.keys(fieldErrors).length > 0;

  /** 越界（数值本身合法）时在 blur 钳制到最近合法边界，并显式播报，绝不静默改写。 */
  const clampOnBlur = (key: FieldKey, text: string, set: (v: string) => void): void => {
    const r = parseIntField(text, FIELD_RANGE[key]);
    if (r.kind !== 'range' || r.clampTo === null) {
      setClamped((m) => (m[key] ? { ...m, [key]: '' } : m));
      return;
    }
    set(String(r.clampTo));
    setClamped((m) => ({
      ...m,
      [key]: `已把${FIELD_RANGE[key].label}钳制到合法${r.clampTo === FIELD_RANGE[key].min ? '下限' : '上限'} ${r.clampTo}`,
    }));
    // REG2-NEW-001：本次钳制还没被用户确认过，提交要先停一拍（见 submit）。
    setClampAck(false);
  };

  /**
   * R2 聚焦：pristine（用户没编辑过）的框聚焦时清空为 ''，原默认值降为 placeholder 灰字，
   * 用户可直接输入新值（省一步删除）。dirty 的框不清空，仅进入编辑态。
   */
  const onFocusField = (key: FieldKey, set: (v: string) => void) => (): void => {
    if (pristine[key]) {
      set('');
      setRestored((m) => (m[key] ? { ...m, [key]: '' } : m));
    }
  };

  /**
   * R2 失焦：值为空 → 回填默认值（= placeholder 数值）、置回 pristine，并显式播报「已恢复默认 N」
   * （role=status，语义是「用户没输入任何东西 = 接受默认值」，不是静默改写）；否则走既有越界钳制。
   */
  const onBlurField = (key: FieldKey, text: string, set: (v: string) => void) => (): void => {
    if (text.trim() === '') {
      const d = defaults[key];
      set(d);
      setPristine((m) => ({ ...m, [key]: true }));
      setClamped((m) => (m[key] ? { ...m, [key]: '' } : m));
      setRestored((m) => ({ ...m, [key]: `已恢复默认 ${d}` }));
      return;
    }
    clampOnBlur(key, text, set);
  };

  /** 输入变化：原样记录，置 dirty，同时清掉上一次的钳制 / 恢复默认播报。 */
  const onNum = (key: FieldKey, set: (v: string) => void) => (v: string): void => {
    set(v);
    setPristine((m) => (m[key] ? { ...m, [key]: false } : m));
    setClamped((m) => (m[key] ? { ...m, [key]: '' } : m));
    setRestored((m) => (m[key] ? { ...m, [key]: '' } : m));
    setSubmitError(null);
    setClampAck(false);
  };

  const applyPreset = (p: (typeof PRESETS)[number]): void => {
    setOppText(String(p.opponents));
    setStackText(String(p.stack));
    setSbText(String(p.sb));
    setBbText(String(p.bb));
    setAnteText('0');
    // R2：预设写入的值同样视为 pristine（聚焦仍可一键清空），并成为各框的新默认值 / placeholder。
    setDefaults({ opponents: String(p.opponents), stack: String(p.stack), sb: String(p.sb), bb: String(p.bb), ante: '0' });
    setPristine({ opponents: true, stack: true, sb: true, bb: true, ante: true });
    setRestored({});
    setClamped({});
    setClampAck(false);
    setSubmitError(null);
  };

  /**
   * Round21-M1 · AC-Z3：零键盘设置总人数（= 对手数 + 1）。由 −/+ 步进器与人数芯片调用，
   * 全程不聚焦任何 input（避免 iOS auto-zoom），直接写 `oppText`。写入即视为已编辑（dirty=false 语义
   * 上仍是「用户显式选择」），并清掉上一次的钳制/恢复播报，保持与手输路径同一套校验口径。
   */
  const setTotalPeople = (total: number): void => {
    const clamped = Math.min(TOTAL_MAX, Math.max(TOTAL_MIN, Math.round(total)));
    const opp = clamped - 1;
    setOppText(String(opp));
    setPristine((m) => ({ ...m, opponents: false }));
    setClamped((m) => (m.opponents ? { ...m, opponents: '' } : m));
    setRestored((m) => (m.opponents ? { ...m, opponents: '' } : m));
    setSubmitError(null);
    setClampAck(false);
  };
  /** 当前总人数（对手数合法时 = 对手 + 1，否则回落默认对手数 + 1），供步进器读数/边界禁用。 */
  const currentTotal = ((opponents ?? Number(defaults.opponents)) || 1) + 1;

  const submit = (): void => {
    setSubmitError(null);
    if (invalid || opponents === null || stack === null || sb === null || bb === null || ante === null) {
      setSubmitError('开局设置还有不合法的字段，请先按提示修正。');
      return;
    }
    /*
     * REG2-NEW-001：「失焦钳制」与「点击开始」的时序竞态。
     *
     * 浏览器把一次点击拆成 mousedown → blur → click：blur 先把 `0` 钳制成 `1`，
     * 于是 click 读到的是**钳制后的合法值**，用户以为开的是「0 对手」，实际开出 2 人桌，
     * 而唯一的提示（`.field__clamp` 播报）随着页面切到牌桌一起消失，等于无提示。
     *
     * 修法按 QA 建议走 commit-then-validate，并在「本次交互刚发生过钳制且用户还没确认」时
     * 停一拍：把调整结果升到 role=alert 明确播报，再次点击才真正开局。
     * 只拦一次、且只在有钳制时拦，正常开局路径的点击数不变。
     */
    const notes = FIELD_ORDER.map((k) => clamped[k]).filter((t): t is string => !!t);
    if (notes.length > 0 && !clampAck) {
      setClampAck(true);
      setSubmitError(`${notes.join('；')}。开局将按调整后的数值进行，请确认后再次点击「开始对局」。`);
      return;
    }
    try {
      validateConfig({ seatCount: opponents + 1, startingStack: stack, smallBlind: sb, bigBlind: bb, ante, humanSeat: 0 });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
      return;
    }
    onStart({
      opponents,
      startingStack: stack,
      smallBlind: sb,
      bigBlind: bb,
      ante,
      seed: seedInvalid ? null : seedValue,
      styleMode,
      styles: styles.slice(0, opponents),
    });
  };

  const summary = save.status === 'ok' ? summarize(save.doc) : null;

  return (
    <div className="start">
      <div className="start__body">
        <div className="start__hero">
        {/* 对外文案脱敏（PM 终验收红线）：开局页首屏这一行是唯一的可见品类描述，
            统一改用产品代号 + 「单机人机牌局」这类中性表述，不出现任何可识别品类词。 */}
        <p className="start__eyebrow">Project Riverside · 单机人机牌局</p>
        <h1 className="start__title">开一桌，把每个决策算清楚</h1>
        <p className="start__lead">
          单机人机对战 · 五种 AI 风格 · 精确赢率计算器（只对 1 名随机对手，全枚举无采样）· 手牌复盘
        </p>
        <ul className="start__facts">
          <li>
            <strong className="num">2–8</strong>
            <span>桌上人数</span>
          </li>
          <li>
            <strong className="num">5</strong>
            <span>AI 风格</span>
          </li>
          <li>
            <strong className="num">1,712,304</strong>
            <span>翻牌圈枚举牌面上限</span>
          </li>
          <li>
            <strong className="num">500</strong>
            <span>手牌历史上限</span>
          </li>
        </ul>
      </div>

      <div className="start__grid">
        {summary ? (
          <section className="panel start__resume" aria-label="继续上次对局">
            <h2 className="panel__title">继续上次对局</h2>
            <dl className="kv">
              <div>
                <dt>进行到</dt>
                <dd>
                  第 <span className="num">{summary.handNumber}</span> 手 · {PHASE_CN[summary.phase] ?? summary.phase}
                </dd>
              </div>
              <div>
                <dt>你的筹码</dt>
                <dd className="num">{chips(summary.myStack)}</dd>
              </div>
              <div>
                <dt>桌型</dt>
                <dd>
                  {summary.seatCount} 人 · 盲注 <span className="num">{summary.smallBlind}/{summary.bigBlind}</span>
                </dd>
              </div>
              <div>
                <dt>保存时间</dt>
                <dd>{relativeTime(summary.savedAt)}</dd>
              </div>
            </dl>
            <div className="start__resume-actions">
              <button type="button" className="btn btn--primary" onClick={onResume}>
                继续上次对局
              </button>
              <button type="button" className="btn" onClick={onDiscardSave}>
                放弃存档
              </button>
            </div>
          </section>
        ) : save.status === 'incompatible' || save.status === 'corrupt' ? (
          <section className="panel start__resume" aria-label="存档状态">
            <h2 className="panel__title">上次的存档无法继续</h2>
            <p className="muted">{save.reason}</p>
            <p className="muted">可直接开始新对局，新存档会覆盖旧数据。</p>
            <button type="button" className="btn" onClick={onDiscardSave}>
              清除旧存档
            </button>
          </section>
        ) : null}

        <section className="panel start__setup" aria-label="开局设置">
          <h2 className="panel__title">开局设置</h2>

          <div className="preset-row">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={`preset${opponents === p.opponents && stack === p.stack && bb === p.bb ? ' preset--on' : ''}`}
                onClick={() => applyPreset(p)}
              >
                <span className="preset__label">{p.label}</span>
                <span className="preset__desc">{p.desc}</span>
              </button>
            ))}
          </div>

          {/* AC-Z3：零键盘人数选择（−/+ 步进器覆盖 2~10 每一档 + 常用人数芯片），全程不聚焦 input。 */}
          <div className="field oppstep-field">
            <span className="field__label">桌上总人数（含你）</span>
            <div className="oppstep" role="group" aria-label="桌上总人数（零键盘调整）">
              <button
                type="button"
                className="oppstep__btn"
                aria-label="减少一人"
                disabled={currentTotal <= TOTAL_MIN}
                onClick={() => setTotalPeople(currentTotal - 1)}
              >
                −
              </button>
              <span className="oppstep__val num" aria-live="polite">
                {currentTotal} 人
              </span>
              <button
                type="button"
                className="oppstep__btn"
                aria-label="增加一人"
                disabled={currentTotal >= TOTAL_MAX}
                onClick={() => setTotalPeople(currentTotal + 1)}
              >
                +
              </button>
              <span className="oppstep__chips">
                {[2, 3, 6, 8].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`oppstep__chip${currentTotal === n ? ' oppstep__chip--on' : ''}`}
                    aria-pressed={currentTotal === n}
                    onClick={() => setTotalPeople(n)}
                  >
                    {n}
                  </button>
                ))}
              </span>
            </div>
          </div>

          <div className="form-grid">
            <NumField
              fieldKey="opponents"
              text={oppText}
              error={fieldErrors.opponents}
              clampNote={clamped.opponents}
              restoredNote={restored.opponents}
              placeholder={defaults.opponents}
              errorId={errId('opponents')}
              hint={`桌上总人数 ${opponents === null ? '—' : opponents + 1}（含你，支持 2–8 人）`}
              onChange={onNum('opponents', setOppText)}
              onFocus={onFocusField('opponents', setOppText)}
              onBlur={onBlurField('opponents', oppText, setOppText)}
            />

            <NumField
              fieldKey="stack"
              text={stackText}
              step={100}
              error={fieldErrors.stack}
              clampNote={clamped.stack}
              restoredNote={restored.stack}
              placeholder={defaults.stack}
              errorId={errId('stack')}
              hint={
                <>
                  约 <span className="num">{stack !== null && bb !== null && bb > 0 ? Math.floor(stack / bb) : '—'}</span> 个大盲
                </>
              }
              onChange={onNum('stack', setStackText)}
              onFocus={onFocusField('stack', setStackText)}
              onBlur={onBlurField('stack', stackText, setStackText)}
            />

            <NumField
              fieldKey="sb"
              text={sbText}
              error={fieldErrors.sb}
              clampNote={clamped.sb}
              restoredNote={restored.sb}
              placeholder={defaults.sb}
              errorId={errId('sb')}
              onChange={onNum('sb', setSbText)}
              onFocus={onFocusField('sb', setSbText)}
              onBlur={onBlurField('sb', sbText, setSbText)}
            />

            <NumField
              fieldKey="bb"
              text={bbText}
              error={fieldErrors.bb}
              clampNote={clamped.bb}
              restoredNote={restored.bb}
              placeholder={defaults.bb}
              errorId={errId('bb')}
              onChange={onNum('bb', setBbText)}
              onFocus={onFocusField('bb', setBbText)}
              onBlur={onBlurField('bb', bbText, setBbText)}
            />
          </div>

          <div className="field">
            <span className="field__label">AI 风格构成</span>
            <Segmented
              ariaLabel="AI 风格构成"
              value={styleMode}
              onChange={(v) => setStyleMode(v)}
              options={[
                { value: 'random' as const, label: '随机分配' },
                { value: 'manual' as const, label: '手动指定' },
              ]}
            />
            {styleMode === 'manual' ? (
              <div className="style-list">
                {Array.from({ length: oppSafe }, (_, i) => (
                  <label key={i} className="style-row">
                    <span>电脑{i + 1}</span>
                    <select
                      className="input"
                      value={styles[i]}
                      onChange={(e) => {
                        const next = [...styles];
                        next[i] = e.target.value as StyleKey;
                        setStyles(next);
                      }}
                    >
                      {STYLE_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {STYLES[k].nameCn}
                        </option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
            ) : (
              <p className="field__hint">开局时从紧凶型 / 松凶型 / 保守型 / 跟注站 / 诈唬型中随机分配，并记入存档以便复现。</p>
            )}
          </div>

          <button type="button" className="link-btn" onClick={() => setAdvanced((v) => !v)} aria-expanded={advanced}>
            {advanced ? '收起高级选项' : '展开高级选项（前注 / 随机种子）'}
          </button>

          {advanced ? (
            <div className="form-grid">
              <NumField
                fieldKey="ante"
                text={anteText}
                error={fieldErrors.ante}
                clampNote={clamped.ante}
                restoredNote={restored.ante}
                placeholder={defaults.ante}
                errorId={errId('ante')}
                hint="0 表示不收前注"
                onChange={onNum('ante', setAnteText)}
                onFocus={onFocusField('ante', setAnteText)}
                onBlur={onBlurField('ante', anteText, setAnteText)}
              />
              <label className="field">
                <span className="field__label">随机种子</span>
                <input
                  className="input input--num"
                  type="text"
                  inputMode="numeric"
                  placeholder="留空 = 随机"
                  value={seedText}
                  aria-invalid={seedInvalid}
                  onChange={(e) => setSeedText(e.target.value)}
                />
                <span className="field__hint">同一种子 + 同一动作序列可完整复现牌序与 AI 决策</span>
                {fieldErrors.seed ? <span className="field__error">{fieldErrors.seed}</span> : null}
              </label>
            </div>
          ) : null}

          {submitError ? (
            <p className="field__error" role="alert" style={{ whiteSpace: 'pre-wrap' }}>
              {submitError}
            </p>
          ) : null}

          <button type="button" className="btn btn--primary start__go" disabled={invalid} onClick={submit}>
            开始对局
          </button>
          {invalid ? <p className="field__hint">请先修正上方标红的字段</p> : null}

          {onEnterMultiplayer ? (
            <button type="button" className="btn start__mp" onClick={onEnterMultiplayer}
              style={{ marginTop: 'var(--space-3)', width: '100%' }}>
              👥 多人对战（房间号联机）
            </button>
          ) : null}
        </section>
        </div>
      </div>

      <footer className="start__foot">
        赢率口径：始终为「你 vs 1 名随机对手」的精确值，不随桌上实际人数变化；结果由 Web Worker 全量枚举得出，无采样、无估算。
        <span className="start__version" data-testid="app-version" title="当前部署版本 / commit">{VERSION_LABEL}</span>
      </footer>
    </div>
  );
}

/** 数字字段的取值域与文案（AC3.1：总人数 2–8 → 对手 1–7；Round23 §4.1.5 人数上限降至 8）。 */
const FIELD_RANGE = {
  opponents: { label: '对手数量', min: 1, max: 7 },
  stack: { label: '起始筹码', min: 1, max: 10_000_000 },
  sb: { label: '小盲', min: 1, max: 1_000_000 },
  bb: { label: '大盲', min: 1, max: 2_000_000 },
  ante: { label: '前注', min: 0, max: 1_000_000 },
} as const;

type FieldKey = keyof typeof FIELD_RANGE;
/** 字段遍历顺序：错误汇总与钳制播报都按它走，保证文案顺序稳定可断言。 */
const FIELD_ORDER = ['opponents', 'stack', 'sb', 'bb', 'ante'] as const;
type FieldRange = { readonly label: string; readonly min: number; readonly max: number };

interface ParsedField {
  /** 合法时的整数值，非法时为 null（**不回退旧值**，非法态必须能被 UI 观察到）。 */
  value: number | null;
  /** 非法原因文案，合法时为 null。 */
  error: string | null;
  kind: 'ok' | 'empty' | 'nan' | 'fraction' | 'range';
  /** 仅越界时给出的最近合法边界（blur 钳制用）。 */
  clampTo: number | null;
}

/** 把用户原样输入解析成整数，并区分「空 / 非数字 / 小数 / 越界」四类具体原因。 */
export function parseIntField(raw: string, range: FieldRange): ParsedField {
  const { label, min, max } = range;
  const t = raw.trim();
  if (t === '') return { value: null, error: `请填写${label}（${min}–${max} 的整数）`, kind: 'empty', clampTo: null };
  if (!/^[+-]?\d*(\.\d+)?$/.test(t) || !Number.isFinite(Number(t))) {
    return { value: null, error: `${label}只能是数字，请输入 ${min}–${max} 的整数`, kind: 'nan', clampTo: null };
  }
  const n = Number(t);
  if (!Number.isInteger(n)) return { value: null, error: `${label}必须是整数，不能带小数（当前 ${t}）`, kind: 'fraction', clampTo: null };
  if (n < min) return { value: null, error: `${label}不能小于 ${min}（当前 ${n}）`, kind: 'range', clampTo: min };
  if (n > max) return { value: null, error: `${label}不能大于 ${max}（当前 ${n}）`, kind: 'range', clampTo: max };
  return { value: n, error: null, kind: 'ok', clampTo: null };
}

/**
 * 数字字段：原样保存输入 + 字段级非法原因 + 越界钳制播报。
 * `aria-invalid` / `aria-describedby` 与视觉错误态同源，屏幕阅读器与视觉用户拿到同一条原因。
 */
function NumField({
  fieldKey,
  text,
  hint,
  step,
  error,
  clampNote,
  restoredNote,
  placeholder,
  errorId,
  onChange,
  onFocus,
  onBlur,
  children,
}: {
  fieldKey: FieldKey;
  text: string;
  hint?: React.ReactNode;
  step?: number;
  error?: string;
  clampNote?: string;
  restoredNote?: string;
  placeholder?: string;
  errorId: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  /** 额外控件（如 AC-Z3 零键盘步进器），渲染在 hint 之后。 */
  children?: React.ReactNode;
}) {
  const range = FIELD_RANGE[fieldKey];
  return (
    <label className="field">
      <span className="field__label">{fieldKey === 'opponents' ? '对手数量（AI）' : fieldKey === 'ante' ? '前注 Ante' : range.label}</span>
      <input
        className="input input--num"
        type="number"
        min={range.min}
        max={range.max}
        step={step}
        value={text}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {hint ? <span className="field__hint">{hint}</span> : null}
      {children}
      {error ? (
        <span className="field__error" id={errorId}>
          {error}
        </span>
      ) : null}
      {clampNote ? (
        <span className="field__clamp" role="status">
          {clampNote}
        </span>
      ) : null}
      {/* R2：blur 空回填默认值的显式播报（role=status，与钳制播报同源不同因） */}
      {restoredNote ? (
        <span className="field__hint field__restored" role="status">
          {restoredNote}
        </span>
      ) : null}
    </label>
  );
}

function pickLine(msg: string, keys: readonly string[]): string {
  const line = msg
    .split('\n')
    .map((l) => l.replace(/^-\s*/, '').trim())
    .find((l) => keys.some((k) => l.includes(k)));
  return line ?? msg;
}
